'use client';

import { styled } from 'styled-components';
import * as Styled from '@lib/styles';
import { CtaProps, ImageProps } from '@types';
import cs from 'classnames';
import Link from 'next/link';
import ContentfulImage from '@dvd/components/ContentfulImage';
import { trackLink } from '@lib/tracking';

export type PromoColor =
  | 'blue'
  | 'blueDark'
  | 'green'
  | 'greenDark'
  | 'orange'
  | 'orangeDark'
  | 'pink'
  | 'pinkDark'
  | 'purple'
  | 'purpleDark'
  | 'yellow'
  | 'yellowDark'
  | 'light';

export type PromoProps = {
  /**
   * The title of the promo
   */
  title: string;
  /**
   * The description of the promo
   */
  description: string;
  /**
   * The cta of the promo
   */
  cta: CtaProps;
  /**
   * The color scheme of the promo
   */
  colorScheme: PromoColor;
  /**
   * The image of the promo; its height follows the image’s aspect ratio.
   */
  image: Pick<ImageProps, 'src' | 'alt'>;
  /**
   * Optional pill text shown above the title. When omitted, no pill is rendered.
   */
  pillText?: string;
};

// Note: This component mimics the behaviour of TextLink. If the TextLink component changes this component will need to change accordingly.
const PromoFauxLink = styled(Styled.BodyMd)<{ $colorScheme: PromoColor }>`
  color: ${({ theme, $colorScheme }) => theme.colorSchemes[$colorScheme].link};
  text-decoration: underline transparent;
  transition:
    text-decoration-color var(--global-motion),
    color var(--global-motion);
  font-weight: ${({ theme }) => theme.typography.fontWeight.headingOne};

  &::after {
    content: '→';
    display: inline-block;
    padding-inline-start: ${({ theme }) => theme.spacing['0.25']};
    text-decoration: underline transparent;
    transition: transform var(--global-motion);
  }
`;

const PromoPill = styled(Styled.BodyXs)`
  background-color: ${({ theme }) => theme.colors.blue500};
  border-radius: 6.25rem;
  padding-block: ${({ theme }) => theme.spacing[0.25]};
  padding-inline: ${({ theme }) => theme.spacing[0.75]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.eyebrow};
  width: fit-content;
  margin-block-end: ${({ theme }) => theme.spacing[0.75]};
  transition: box-shadow var(--global-motion);

  &.blueDark-pill {
    background-color: ${({ theme }) => theme.colors.blue200};
    color: ${({ theme }) => theme.colors.blue800};
  }

  &.greenDark-pill {
    background-color: ${({ theme }) => theme.colors.green300};
    color: ${({ theme }) => theme.colors.green800};
  }

  &.orangeDark-pill {
    background-color: ${({ theme }) => theme.colors.orange200};
    color: ${({ theme }) => theme.colors.orange800};
  }

  &.pinkDark-pill {
    background-color: ${({ theme }) => theme.colors.pink300};
    color: ${({ theme }) => theme.colors.pink800};
  }

  &.purpleDark-pill {
    background-color: ${({ theme }) => theme.colors.purple300};
    color: ${({ theme }) => theme.colors.purple800};
  }

  &.yellowDark-pill {
    background-color: ${({ theme }) => theme.colors.yellow200};
    color: ${({ theme }) => theme.colors.yellow800};
  }

  &.light-pill {
    background-color: ${({ theme }) => theme.colors.blue200};
    color: ${({ theme }) => theme.colors.blue800};
  }

  &.blue-pill {
    background-color: ${({ theme }) => theme.colors.blue600};
    color: ${({ theme }) => theme.colors.white};
  }

  &.green-pill {
    background-color: ${({ theme }) => theme.colors.green600};
    color: ${({ theme }) => theme.colors.white};
  }

  &.orange-pill {
    background-color: ${({ theme }) => theme.colors.orange600};
    color: ${({ theme }) => theme.colors.white};
  }

  &.pink-pill {
    background-color: ${({ theme }) => theme.colors.pink600};
    color: ${({ theme }) => theme.colors.white};
  }

  &.purple-pill {
    background-color: ${({ theme }) => theme.colors.purple600};
    color: ${({ theme }) => theme.colors.white};
  }

  &.yellow-pill {
    background-color: ${({ theme }) => theme.colors.yellow400};
    color: ${({ theme }) => theme.colors.yellow800};
  }
`;

// Note: This is a visual shadow, distinct to this module, and not related to
// any elevation concept/tokens
// TODO: abstract to a token if desired for other uses
const PILL_GLOW = '0 0 10px 0';

const PromoContainer = styled(Link)<{ $colorScheme: PromoColor }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.radius.std};
  background-color: ${({ theme, $colorScheme }) =>
    theme.colorSchemes[$colorScheme].bg};
  color: ${({ theme, $colorScheme }) => theme.colorSchemes[$colorScheme].fg};
  gap: ${({ theme }) => theme.spacing[2.5]};
  transition: box-shadow var(--global-motion);

  &:hover {
    box-shadow: ${({ theme }) => theme.elevation.elevation1()};

    ${PromoFauxLink}::after {
      transform: translateX(${({ theme }) => theme.spacing['0.25']});
    }

    // Custom box-shadow colors for each pill
    ${PromoPill}.blueDark-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.blue200};
    }

    ${PromoPill}.greenDark-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.green200};
    }

    ${PromoPill}.orangeDark-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.orange200};
    }

    ${PromoPill}.pinkDark-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.pink200};
    }

    ${PromoPill}.purpleDark-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.purple200};
    }

    ${PromoPill}.yellowDark-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.yellow200};
    }

    ${PromoPill}.light-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.blue200};
    }

    ${PromoPill}.blue-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.blue300};
    }

    ${PromoPill}.green-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.green300};
    }

    ${PromoPill}.orange-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.orange300};
    }

    ${PromoPill}.pink-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.pink300};
    }

    ${PromoPill}.purple-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.purple300};
    }

    ${PromoPill}.yellow-pill {
      box-shadow: ${PILL_GLOW} ${({ theme }) => theme.colors.yellow300};
    }
  }

  &:hover,
  &:active {
    ${PromoFauxLink} {
      text-decoration: underline currentcolor;
      color: ${({ theme, $colorScheme }) =>
        theme.colorSchemes[$colorScheme].hover};
    }
  }
`;

const PromoContent = styled.div`
  flex: 1;
`;

const PromoTitle = styled.h3`
  margin-block-end: ${({ theme }) => theme.spacing[0.75]};
`;

const PromoDescription = styled.p`
  margin-block-end: ${({ theme }) => theme.spacing[0.75]};

  // Override global link font weight
  font-weight: ${({ theme }) => theme.typography.fontWeight.body};
`;

// Preserve the established desktop footprint independently of source resolution.
const PROMO_IMAGE_WIDTH = '24rem';
const PROMO_RETINA_IMAGE_WIDTH = '20rem';

const PromoImage = styled(ContentfulImage)`
  display: none;
  border-radius: ${({ theme }) => theme.radius.std};
  height: auto;

  ${({ theme }) => theme.mq.minWidth.large} {
    display: block;
    flex: 0 0 ${PROMO_IMAGE_WIDTH};
    min-width: 0;
    width: ${PROMO_IMAGE_WIDTH};
    height: auto;

    @media (resolution > 1dppx) {
      flex-basis: ${PROMO_RETINA_IMAGE_WIDTH};
      width: ${PROMO_RETINA_IMAGE_WIDTH};
    }
  }
`;

const Promo = ({
  title,
  description,
  cta,
  colorScheme,
  image,
  pillText,
}: PromoProps) => {
  const onClick = () => {
    if (cta.trackingLabel && cta.trackingProperty) {
      trackLink(cta.trackingLabel, cta.trackingProperty);
    }
  };

  return (
    <PromoContainer
      $colorScheme={colorScheme}
      href={cta.href}
      onClick={onClick}
    >
      <PromoImage src={image.src} alt={image.alt} width={304} height={189} />
      <PromoContent>
        {pillText && (
          <PromoPill className={cs(`${colorScheme}-pill`)}>
            {pillText}
          </PromoPill>
        )}
        <PromoTitle>{title}</PromoTitle>
        <PromoDescription>{description}</PromoDescription>
        <PromoFauxLink $colorScheme={colorScheme}>{cta.text}</PromoFauxLink>
      </PromoContent>
    </PromoContainer>
  );
};

export default Promo;
